import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Package,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
} from '../services/api';
import './InventoryPage.css';

function InventoryPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: 'un',
    cost: '',
    stock: '',
    minStock: '',
    supplierId: '',
    expirationDate: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formErrors, setFormErrors] = useState({});
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const response = await getInventoryItems();
      setItems(response.items || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        unit: item.unit,
        cost: item.cost,
        stock: item.stock,
        minStock: item.minStock,
        supplierId: item.supplierId || '',
        expirationDate: item.expirationDate ? item.expirationDate.split('T')[0] : '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        unit: 'un',
        cost: '',
        stock: '',
        minStock: '',
        supplierId: '',
        expirationDate: '',
      });
    }
    setFormErrors({});
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.name.trim()) errors.name = t('customers.validation.nameRequired');
    if (!formData.cost && formData.cost !== 0) errors.cost = t('inventory.costRequired');
    if (!formData.stock && formData.stock !== 0) errors.stock = t('inventory.stockRequired');
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const payload = { ...formData };
      if (!payload.expirationDate) delete payload.expirationDate; // Send null or undefined if empty

      if (editingItem) {
        await updateInventoryItem(editingItem.itemId, payload);
      } else {
        await createInventoryItem(payload);
      }
      setModalOpen(false);
      loadItems();
      showFeedback('success', t('inventory.saveSuccess'));
    } catch (error) {
      console.error('Error saving item:', error);
      showFeedback('error', t('inventory.saveError'));
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        await deleteInventoryItem(itemId);
        loadItems();
        showFeedback('success', t('inventory.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting item:', error);
        showFeedback('error', t('inventory.deleteError'));
      }
    }
  };

  const handleQuickStockUpdate = async (item, change) => {
    try {
      const newStock = Math.max(0, parseFloat(item.stock) + change);
      await updateInventoryItem(item.itemId, { stock: newStock });
      // Optimistic update
      setItems((prev) =>
        prev.map((i) => (i.itemId === item.itemId ? { ...i, stock: newStock } : i))
      );
    } catch (error) {
      console.error('Error updating stock:', error);
      loadItems(); // Revert on error
    }
  };

  // Helper to check expiration status
  const getExpirationStatus = (dateStr) => {
    if (!dateStr) return null;
    const todayUTC = Date.UTC(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    const expUTC = Date.UTC(
      new Date(dateStr).getUTCFullYear(),
      new Date(dateStr).getUTCMonth(),
      new Date(dateStr).getUTCDate()
    );

    const diffTime = expUTC - todayUTC;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'expired';
    if (diffDays <= 3) return 'critical';
    if (diffDays <= 7) return 'warning';
    return 'ok';
  };

  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    },
    [sortColumn]
  );

  const filteredAndSortedItems = useMemo(() => {
    let result = items;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) => item.name.toLowerCase().includes(query) || item.unit.toLowerCase().includes(query)
      );
    }

    if (statusFilter === 'lowStock') {
      result = result.filter((item) => item.stock <= item.minStock);
    } else if (statusFilter === 'expiring') {
      result = result.filter((item) => {
        const status = getExpirationStatus(item.expirationDate);
        return status === 'expired' || status === 'critical' || status === 'warning';
      });
    }

    result = [...result].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'cost' || sortColumn === 'stock') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortColumn === 'expirationDate') {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [items, searchQuery, statusFilter, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedItems.length / itemsPerPage));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedItems.slice(start, start + itemsPerPage);
  }, [filteredAndSortedItems, currentPage, itemsPerPage]);

  const SortIcon = useCallback(
    ({ column }) => {
      if (sortColumn !== column) return <ChevronsUpDown size={14} />;
      return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    },
    [sortColumn, sortDirection]
  );

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="inventory-page">
      <div className="page-header">
        <h1>{t('inventory.title', 'Inventario')}</h1>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          {t('inventory.addItem', 'Agregar Item')}
        </button>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('inventory.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
        <div className="filter-buttons">
          <button
            type="button"
            className={`filter-btn${statusFilter === 'all' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('all');
              setCurrentPage(1);
            }}
          >
            {t('common.all')}
          </button>
          <button
            type="button"
            className={`filter-btn${statusFilter === 'lowStock' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('lowStock');
              setCurrentPage(1);
            }}
          >
            {t('inventory.lowStock')}
          </button>
          <button
            type="button"
            className={`filter-btn${statusFilter === 'expiring' ? ' active' : ''}`}
            onClick={() => {
              setStatusFilter('expiring');
              setCurrentPage(1);
            }}
          >
            {t('inventory.expiring')}
          </button>
        </div>
      </div>

      <div className="card">
        {filteredAndSortedItems.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <p>{t('inventory.emptyState')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSort('name')}>
                    {t('inventory.name', 'Nombre')} <SortIcon column="name" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('stock')}>
                    {t('inventory.stock', 'Stock')} <SortIcon column="stock" />
                  </th>
                  <th>{t('inventory.unit', 'Unidad')}</th>
                  <th className="sortable-th" onClick={() => handleSort('expirationDate')}>
                    {t('inventory.expiration', 'Vencimiento')} <SortIcon column="expirationDate" />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('cost')}>
                    {t('inventory.cost', 'Costo')} <SortIcon column="cost" />
                  </th>
                  <th>{t('inventory.status', 'Estado')}</th>
                  <th>{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => {
                  const expStatus = getExpirationStatus(item.expirationDate);
                  return (
                    <tr key={item.itemId}>
                      <td data-label={t('inventory.name', 'Nombre')}>{item.name}</td>
                      <td data-label={t('inventory.stock', 'Stock')}>
                        <div className="stock-control">
                          <button
                            type="button"
                            className="btn-icon small"
                            onClick={() => handleQuickStockUpdate(item, -1)}
                          >
                            -
                          </button>
                          <span className={item.stock <= item.minStock ? 'text-danger' : ''}>
                            {item.stock}
                          </span>
                          <button
                            type="button"
                            className="btn-icon small"
                            onClick={() => handleQuickStockUpdate(item, 1)}
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td data-label={t('inventory.unit', 'Unidad')}>{item.unit}</td>
                      <td data-label={t('inventory.expiration', 'Vencimiento')}>
                        {item.expirationDate ? (
                          <span className={`expiration-badge ${expStatus}`}>
                            {new Date(item.expirationDate).toLocaleDateString(undefined, {
                              timeZone: 'UTC',
                            })}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td data-label={t('inventory.cost', 'Costo')}>${item.cost}</td>
                      <td data-label={t('inventory.status', 'Estado')}>
                        {item.stock === 0 ? (
                          <span className="badge badge-danger">
                            {t('inventory.outOfStock', 'Sin Stock')}
                          </span>
                        ) : item.stock <= item.minStock ? (
                          <span className="badge badge-warning">
                            {t('inventory.lowStock', 'Stock Bajo')}
                          </span>
                        ) : expStatus === 'critical' || expStatus === 'warning' ? (
                          <span className="badge badge-warning">
                            {t('inventory.expiringSoon', 'Por Vencer')}
                          </span>
                        ) : expStatus === 'expired' ? (
                          <span className="badge badge-danger">
                            {t('inventory.expired', 'Vencido')}
                          </span>
                        ) : (
                          <span className="badge badge-success">
                            {t('inventory.normalStock', 'Normal')}
                          </span>
                        )}
                      </td>
                      <td data-label={t('common.actions', 'Acciones')}>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleOpenModal(item)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleDelete(item.itemId)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="table-pagination">
              <div className="pagination-info">
                {t('inventory.pagination.showing', {
                  from: Math.min(
                    (currentPage - 1) * itemsPerPage + 1,
                    filteredAndSortedItems.length
                  ),
                  to: Math.min(currentPage * itemsPerPage, filteredAndSortedItems.length),
                  total: filteredAndSortedItems.length,
                  defaultValue: '{{from}}–{{to}} de {{total}}',
                })}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-per-page"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  aria-label={t('inventory.pagination.perPage', 'Items por página')}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button
                  type="button"
                  className="btn-icon pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  aria-label={t('common.back', 'Anterior')}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="pagination-page">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-icon pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label={t('inventory.pagination.next', 'Siguiente')}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {editingItem
                  ? t('inventory.editItem', 'Editar Item')
                  : t('inventory.newItem', 'Nuevo Item')}
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>
                <div className={`form-group${formErrors.name ? ' has-error' : ''}`}>
                  <label>{t('inventory.name', 'Nombre')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: '' }));
                      setFormData({ ...formData, name: e.target.value });
                    }}
                    aria-required="true"
                    aria-invalid={!!formErrors.name}
                  />
                  {formErrors.name && (
                    <span className="field-error" role="alert">
                      {formErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('inventory.unit', 'Unidad')}</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    >
                      <option value="un">Unidad (un)</option>
                      <option value="kg">Kilogramo (kg)</option>
                      <option value="lt">Litro (lt)</option>
                      <option value="gr">Gramo (gr)</option>
                      <option value="ml">Mililitro (ml)</option>
                    </select>
                  </div>
                  <div className={`form-group${formErrors.cost ? ' has-error' : ''}`}>
                    <label>{t('inventory.cost', 'Costo')} *</label>
                    <input
                      type="number"
                      value={formData.cost}
                      onChange={(e) => {
                        if (formErrors.cost) setFormErrors((prev) => ({ ...prev, cost: '' }));
                        setFormData({ ...formData, cost: e.target.value });
                      }}
                      min="0"
                      step="0.01"
                      aria-required="true"
                      aria-invalid={!!formErrors.cost}
                    />
                    {formErrors.cost && (
                      <span className="field-error" role="alert">
                        {formErrors.cost}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className={`form-group${formErrors.stock ? ' has-error' : ''}`}>
                    <label>{t('inventory.stock', 'Stock Actual')} *</label>
                    <input
                      type="number"
                      value={formData.stock}
                      onChange={(e) => {
                        if (formErrors.stock) setFormErrors((prev) => ({ ...prev, stock: '' }));
                        setFormData({ ...formData, stock: e.target.value });
                      }}
                      min="0"
                      step="0.01"
                      aria-required="true"
                      aria-invalid={!!formErrors.stock}
                    />
                    {formErrors.stock && (
                      <span className="field-error" role="alert">
                        {formErrors.stock}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('inventory.minStock', 'Stock Mínimo')}</label>
                    <input
                      type="number"
                      value={formData.minStock}
                      onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('inventory.expiration', 'Fecha de Vencimiento (Opcional)')}</label>
                  <input
                    type="date"
                    value={formData.expirationDate}
                    onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                  />
                  <small className="help-text">
                    Si se establece, se generarán alertas 3 días antes del vencimiento.
                  </small>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
